/**
 * Hours Calculator — CAO Schoonmaak compliant calculations
 *
 * Covers: netto-uren, loonperiode, toeslagen (Art. 22), overuren (173u),
 * vakantie-uren (10% opbouw), pauze-aftrek per shift type.
 */

import { isHoliday } from './dutch-holidays';
import { isHolidayLeaveType } from './leave-types';
import { calculateBreak, resolveShiftType, type ShiftType, type BreakResult } from './break-policy';
import type { WerkroosterDay } from '../stores/workforce-store';

// ── Types ──────────────────────────────────────────────────────────

export interface PayPeriod {
  label: string;       // e.g. "maart 2026"
  from: string;        // YYYY-MM-DD (20e)
  to: string;          // YYYY-MM-DD (19e volgende maand)
  month: number;       // 0-indexed month of the "from" date
  year: number;
}

export interface SurchargeResult {
  normalHours: number;
  surchargeHours: number;   // hours with toeslag
  surchargeAmount: number;  // EUR
  breakdown: { slot: string; hours: number; percentage: number; amount: number }[];
}

export interface OvertimeResult {
  workedHours: number;
  paidHours: number;      // min(workedHours, 173)
  overtimeSurplus: number; // hours > 173 → overurenpotje
}

export interface VacationAccrual {
  totalHours: number;       // 10% of paid hours
  legalHours: number;       // 7.69%
  extraLegalHours: number;  // 2.31%
}

export interface StaffPeriodSummary {
  staffId: string;
  period: PayPeriod;
  scheduledHours: number;
  workedHours: number;
  paidHours: number;
  overtimeSurplus: number;
  surcharges: SurchargeResult;
  vacationAccrual: VacationAccrual;
  leaveHours: number;
  sickHours: number;
}

// ── Pay Period (20e-19e) ───────────────────────────────────────────

const MONTH_NAMES = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december'
];

/**
 * Get the pay period (loonperiode) for a given date.
 * Period "maart 2026" = 20 mrt 2026 t/m 19 apr 2026
 */
export function getPayPeriod(date: Date | string): PayPeriod {
  const d = typeof date === 'string' ? new Date(date + 'T00:00:00') : new Date(date);
  const day = d.getDate();

  let periodMonth: number;
  let periodYear: number;

  if (day >= 20) {
    // On or after the 20th → this month's period
    periodMonth = d.getMonth();
    periodYear = d.getFullYear();
  } else {
    // Before the 20th → previous month's period
    const prev = new Date(d);
    prev.setMonth(prev.getMonth() - 1);
    periodMonth = prev.getMonth();
    periodYear = prev.getFullYear();
  }

  // Period start: 20th of periodMonth
  const fromDate = new Date(periodYear, periodMonth, 20);
  // Period end: 19th of next month
  const toDate = new Date(periodYear, periodMonth + 1, 19);

  // Label = next month (loonperiode 20 jan–19 feb = "februari")
  const labelMonth = (periodMonth + 1) % 12;
  const labelYear = periodMonth === 11 ? periodYear + 1 : periodYear;

  return {
    label: `${MONTH_NAMES[labelMonth]} ${labelYear}`,
    from: formatDate(fromDate),
    to: formatDate(toDate),
    month: periodMonth,
    year: periodYear,
  };
}

/**
 * Get all pay periods for a year (Januari t/m December).
 * "Januari [year]" = 20 dec vorig jaar t/m 19 jan [year].
 * "December [year]" = 20 nov t/m 19 dec [year].
 */
export function getPayPeriodsForYear(year: number): PayPeriod[] {
  const periods: PayPeriod[] = [];
  for (let m = 0; m < 12; m++) {
    periods.push(getPayPeriod(new Date(year, m, 20)));
  }
  return periods;
}

// ── Break Minutes (canonieke policy: src/lib/break-policy.ts) ──────

/**
 * Normaliseer een vrij shift-type (UI-label, team of schedule-waarde) naar
 * de canonieke ShiftType. 'Chauffeur' is het UI-label voor route-shifts.
 */
export function normalizeShiftType(raw: string | null | undefined): ShiftType {
  const v = (raw || '').toLowerCase();
  if (v === 'wasserij') return 'wasserij';
  if (v === 'route' || v === 'chauffeur') return 'route';
  if (v === 'kantoor') return 'kantoor';
  return 'algemeen';
}

function grossHoursBetween(timeFrom: string, timeTo: string): number {
  const parts = timeFrom.split(':').map(Number);
  const parts2 = timeTo.split(':').map(Number);
  const hFrom = parts[0] || 0, mFrom = parts[1] || 0;
  const hTo = parts2[0] || 0, mTo = parts2[1] || 0;
  return (hTo + mTo / 60) - (hFrom + mFrom / 60);
}

/**
 * Pauze (duur-gebaseerd, canonieke policy) voor een tijdrange.
 * Zelfde regels als de klok bij uitklokken gebruikt.
 */
export function breakForRange(shiftType: string | null | undefined, timeFrom: string | null, timeTo: string | null): BreakResult {
  if (!timeFrom || !timeTo) return { breakMinutes: 0, paidBreakMinutes: 0 };
  const gross = grossHoursBetween(timeFrom, timeTo);
  if (gross <= 0) return { breakMinutes: 0, paidBreakMinutes: 0 };
  return calculateBreak(normalizeShiftType(shiftType), gross);
}

// ── Netto Hours ────────────────────────────────────────────────────

/**
 * Calculate netto hours from a time range minus break (canonieke policy).
 */
export function calculateNettoHours(
  timeFrom: string,
  timeTo: string,
  shiftType: string | null
): number {
  const bruto = grossHoursBetween(timeFrom, timeTo);
  const { breakMinutes } = breakForRange(shiftType, timeFrom, timeTo);
  return Math.max(0, bruto - breakMinutes / 60);
}

// ── CAO Surcharges (Art. 22) ───────────────────────────────────────

/**
 * CAO Schoonmaak Art. 22 surcharge matrix
 * Returns percentage as decimal (0.30 = 30%)
 */
function getSurchargePercentage(dayOfWeek: number, hour: number, minute: number, isHolidayDay: boolean): number {
  if (isHolidayDay) return 1.50; // 150%

  const timeDecimal = hour + minute / 60;

  // Saturday (6) and Sunday (0)
  if (dayOfWeek === 0 || dayOfWeek === 6) return 0.50; // 50%

  // Monday (1)
  if (dayOfWeek === 1) {
    if (timeDecimal < 6) return 0.50;     // 00:00-06:00: 50%
    if (timeDecimal < 21.5) return 0;     // 06:00-21:30: 0%
    return 0.30;                           // 21:30-24:00: 30%
  }

  // Tuesday-Thursday (2-4)
  if (dayOfWeek >= 2 && dayOfWeek <= 4) {
    if (timeDecimal < 6) return 0.30;     // 00:00-06:00: 30%
    if (timeDecimal < 21.5) return 0;     // 06:00-21:30: 0%
    return 0.30;                           // 21:30-24:00: 30%
  }

  // Friday (5)
  if (dayOfWeek === 5) {
    if (timeDecimal < 6) return 0.30;     // 00:00-06:00: 30%
    if (timeDecimal < 21.5) return 0;     // 06:00-21:30: 0%
    return 0.50;                           // 21:30-24:00: 50%
  }

  return 0;
}

/**
 * Calculate CAO surcharges for a single shift
 * Splits hours across time slots and calculates per-slot toeslag.
 * Pauze-aftrek: bruto-blokken worden pro-rata geschaald naar netto, zodat
 * toeslag-uren nooit meer kunnen zijn dan uitbetaalde uren.
 */
export function calculateSurcharges(
  clockIn: string,   // HH:mm
  clockOut: string,  // HH:mm
  dateStr: string,   // YYYY-MM-DD
  basisuurloon: number,
  breakMinutes: number = 0,
  paidBreakMinutes: number = 0,
): SurchargeResult {
  const holiday = isHoliday(dateStr);
  const isHolidayDay = !!holiday;
  const date = new Date(dateStr + 'T00:00:00');
  const dayOfWeek = date.getDay();

  const inParts = clockIn.split(':').map(Number);
  const outParts = clockOut.split(':').map(Number);
  const hIn = inParts[0] || 0, mIn = inParts[1] || 0;
  const hOut = outParts[0] || 0, mOut = outParts[1] || 0;

  // Process per 15-minute block
  const breakdown: Map<string, { hours: number; percentage: number }> = new Map();
  let normalHours = 0;
  let surchargeHours = 0;
  let surchargeAmount = 0;

  const startMinutes = hIn * 60 + mIn;
  const endMinutes = hOut * 60 + mOut;

  for (let m = startMinutes; m < endMinutes; m += 15) {
    const blockHour = Math.floor(m / 60);
    const blockMinute = m % 60;
    const blockDuration = Math.min(15, endMinutes - m) / 60; // in hours

    const percentage = getSurchargePercentage(dayOfWeek, blockHour, blockMinute, isHolidayDay);

    if (percentage > 0) {
      surchargeHours += blockDuration;
      const amount = basisuurloon * percentage * blockDuration;
      surchargeAmount += amount;

      const slotKey = `${Math.round(percentage * 100)}%`;
      const existing = breakdown.get(slotKey) || { hours: 0, percentage };
      existing.hours += blockDuration;
      breakdown.set(slotKey, existing);
    } else {
      normalHours += blockDuration;
    }
  }

  // Pauze-aftrek: scale bruto-slots naar netto zodat toeslag nooit > paidHours kan worden.
  // Alleen het onbetaalde deel van de pauze telt (route >10u heeft 15 min doorbetaald).
  const brutoHours = Math.max(0, (endMinutes - startMinutes) / 60);
  const unpaidBreakHours = Math.max(0, (breakMinutes - paidBreakMinutes) / 60);
  const nettoHours = Math.max(0, brutoHours - unpaidBreakHours);
  const scale = brutoHours > 0 ? nettoHours / brutoHours : 1;

  normalHours *= scale;
  surchargeHours *= scale;
  surchargeAmount *= scale;

  return {
    normalHours,
    surchargeHours,
    surchargeAmount,
    breakdown: Array.from(breakdown.entries()).map(([slot, data]) => ({
      slot,
      hours: Math.round(data.hours * scale * 100) / 100,
      percentage: data.percentage,
      amount: Math.round(data.hours * scale * data.percentage * basisuurloon * 100) / 100,
    })),
  };
}

// ── Overtime (173u grens) ──────────────────────────────────────────

/**
 * Calculate overtime: everything above 173u goes to overurenpotje
 */
export function calculateOvertime(workedHours: number, threshold: number = 173): OvertimeResult {
  const paidHours = Math.min(workedHours, threshold);
  const overtimeSurplus = Math.max(0, workedHours - threshold);
  return { workedHours, paidHours, overtimeSurplus };
}

// ── Vacation (CAO Art. 28) ──────────────────────────────────────────

/**
 * Yearly vacation entitlement for vast/min-max contracts.
 * CAO Schoonmaak: 26 vakantiedagen per jaar.
 * Uren per dag = contracturen / 5 werkdagen.
 * Totaal = 26 × (uren_per_week / 5).
 * Fulltime (38u): 26 × 7.6 = 197.6u. (40u: 26 × 8 = 208u)
 */
export function calculateYearlyVacationEntitlement(urenPerWeek: number | null, contractEndDate?: string | null, contractStartDate?: string | null): number {
  const weekHours = urenPerWeek || 38;
  const hoursPerDay = weekHours / 5;
  const baseHours = 26 * hoursPerDay;
  const proRata = getContractProRataFactor(contractEndDate, contractStartDate);
  return Math.round(baseHours * proRata * 100) / 100;
}

function getContractProRataFactor(endDate?: string | null, startDate?: string | null): number {
  if (!endDate && !startDate) return 1;
  const year = new Date().getFullYear();
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);
  const contractStart = startDate ? new Date(startDate + 'T00:00:00') : yearStart;
  const contractEnd = endDate ? new Date(endDate + 'T00:00:00') : yearEnd;
  // Effectief actief venster binnen dit kalenderjaar
  const effectiveStart = contractStart > yearStart ? contractStart : yearStart;
  const effectiveEnd = contractEnd < yearEnd ? contractEnd : yearEnd;
  if (effectiveStart > effectiveEnd) return 0;
  const totalDays = (yearEnd.getTime() - yearStart.getTime()) / 86400000 + 1;
  const activeDays = (effectiveEnd.getTime() - effectiveStart.getTime()) / 86400000 + 1;
  return activeDays / totalDays;
}

/**
 * Calculate vacation hours accrual (10% of paid hours)
 * Used for 0-uren contracts (accrual per hour, not yearly entitlement)
 * Legal: 7.69%, Extra-legal: 2.31%
 */
export function calculateVacationAccrual(paidHours: number): VacationAccrual {
  const legalHours = Math.round(paidHours * 0.0769 * 100) / 100;
  const extraLegalHours = Math.round(paidHours * 0.0231 * 100) / 100;
  return {
    totalHours: Math.round((legalHours + extraLegalHours) * 100) / 100,
    legalHours,
    extraLegalHours,
  };
}

/**
 * Vast/min-max contract: maandnorm o.b.v. contract-/minimumuren.
 * Gewerkt boven de norm → 10% vakantie-opbouw over het meerwerk.
 */
export function calculateMinMaxExtraVacation(workedHours: number, minUrenPerWeek: number): VacationAccrual {
  const monthlyNorm = minUrenPerWeek * 52 / 12; // ~86.7u voor 20u/week
  const excess = Math.max(0, workedHours - monthlyNorm);
  return calculateVacationAccrual(excess);
}

// ── Scheduled Hours & Daily Saldo ──────────────────────────────────

export interface DailyScheduleEntry {
  date: string;
  expected: number;      // Verwachte netto uren uit rooster
  actual: number;        // Werkelijk geklokt
  leave: number;         // Verlof/ziekte uren
  delta: number;         // actual + leave - expected (+ = over, - = min)
  cumulativeDelta: number; // Lopend saldo
  isHoliday: boolean;
  isLeaveDay: boolean;
  // Uitleg-metadata voor de UI: hoe kwam "verwacht" tot stand?
  roosterFrom?: string;
  roosterTo?: string;
  brutoHours?: number;
  breakMinutes?: number;
  shiftType?: ShiftType;
  breakSource?: 'schedule' | 'team_fallback';
}

export interface ScheduledHoursResult {
  totalScheduled: number;      // Som verwachte uren
  totalActual: number;         // Som werkelijke uren
  totalLeave: number;          // Som verlof/ziekte uren
  saldo: number;               // totalActual + totalLeave - totalScheduled
  dailyBreakdown: DailyScheduleEntry[];
}

/**
 * Calculate scheduled (expected) hours per period based on werkrooster.
 * Compares expected vs actual hours per day with cumulative saldo.
 * Only meaningful for vast/min-max contracts with a werkrooster.
 *
 * Verwacht gebruikt exact dezelfde regels als de klok bij uitklokken:
 * de schedule-shift van die dag (indien meegegeven) wint van het team
 * (resolveShiftType), pauze is duur-gebaseerd (calculateBreak).
 */
export function calculateScheduledHours(
  werkrooster: WerkroosterDay[] | null,
  team: string | null,
  periodFrom: string,
  periodTo: string,
  timeEntries: { date: string; total_hours: number | null }[],
  leaveEntries: { date: string; hours: number; leave_type: string }[],
  contractStart?: string | null,
  scheduleEntries?: { date: string; shift_type: string | null }[],
): ScheduledHoursResult {
  if (!werkrooster || werkrooster.length === 0) {
    return { totalScheduled: 0, totalActual: 0, totalLeave: 0, saldo: 0, dailyBreakdown: [] };
  }

  const daily: DailyScheduleEntry[] = [];
  let cumulativeDelta = 0;
  let totalScheduled = 0;
  let totalActual = 0;
  let totalLeave = 0;

  // Geen verwachte uren vóór de datum in dienst
  const effectiveFrom = contractStart && contractStart > periodFrom ? contractStart : periodFrom;
  const start = new Date(effectiveFrom + 'T00:00:00');
  const end = new Date(periodTo + 'T00:00:00');

  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const dateStr = formatDate(d);
    const dayOfWeek = d.getDay(); // 0=zo, 1=ma, ...
    const holiday = !!isHoliday(dateStr);

    // Verlof/ziekte op deze dag (exclude feestdag — already handled by isHoliday)
    const dayLeave = leaveEntries.filter(l => l.date === dateStr && !isHolidayLeaveType(l.leave_type));
    const rawLeaveHours = dayLeave.reduce((sum, l) => sum + l.hours, 0);
    const isLeaveDay = dayLeave.length > 0;

    // Werkelijke uren
    const dayEntries = timeEntries.filter(te => te.date === dateStr);
    const actual = dayEntries.reduce((sum, te) => sum + (te.total_hours || 0), 0);

    // Verwachte uren: werkrooster voor deze weekdag, pauze via canonieke policy
    const roosterDay = werkrooster.find(w => w.day === dayOfWeek);
    let expected = 0;
    let dayMeta: Pick<DailyScheduleEntry, 'roosterFrom' | 'roosterTo' | 'brutoHours' | 'breakMinutes' | 'shiftType' | 'breakSource'> = {};

    if (roosterDay && !holiday) {
      const scheduleForDay = scheduleEntries?.find(s => s.date === dateStr);
      const { type: shiftType, source } = resolveShiftType({
        scheduleShiftType: scheduleForDay?.shift_type,
        staffTeam: team,
      });
      const bruto = Math.max(0, grossHoursBetween(roosterDay.from, roosterDay.to));
      const { breakMinutes } = calculateBreak(shiftType, bruto);
      expected = Math.max(0, bruto - breakMinutes / 60);
      dayMeta = {
        roosterFrom: roosterDay.from,
        roosterTo: roosterDay.to,
        brutoHours: bruto,
        breakMinutes,
        shiftType,
        breakSource: source,
      };
    }

    // Cap verlofuren op verwachte uren: verlof kan nooit meer bijdragen dan verwacht
    // Dit voorkomt vals positief saldo door ziekte op feestdagen of te hoge verlofuren
    const leaveHours = Math.min(rawLeaveHours, expected);

    const delta = actual + leaveHours - expected;
    cumulativeDelta += delta;

    totalScheduled += expected;
    totalActual += actual;
    totalLeave += leaveHours;

    // Alleen dagen met activiteit of verwachting opnemen
    if (expected > 0 || actual > 0 || leaveHours > 0) {
      daily.push({
        date: dateStr,
        expected,
        actual,
        leave: leaveHours,
        delta,
        cumulativeDelta,
        isHoliday: holiday,
        isLeaveDay,
        ...dayMeta,
      });
    }
  }

  return {
    totalScheduled,
    totalActual,
    totalLeave,
    saldo: cumulativeDelta,
    dailyBreakdown: daily,
  };
}

/**
 * Werkdagen uit het rooster voor wie geen uren registreert (vast salaris):
 * elke roosterdag in de periode (vanaf datum in dienst) die geen feestdag is
 * en niet volledig door verlof of ziekte wordt gedekt. De accountant heeft het
 * aantal gewerkte dagen nodig voor de loonaangifte, ook zonder klok; tellen op
 * geklokte dagen gaf 0 (Joery, oktober 2026).
 */
export function countRoosterWerkdagen(sched: ScheduledHoursResult): number {
  return sched.dailyBreakdown.filter(d => d.expected > 0 && d.leave < d.expected).length;
}

// ── Year Saldo (calendar year) ────────────────────────────────────

// Uses MONTH_NAMES defined above

export interface MonthSaldo {
  month: number;           // 0-11
  year: number;
  label: string;           // "januari 2026"
  from: string;            // YYYY-MM-DD (1e van de maand)
  to: string;              // YYYY-MM-DD (laatste dag of vandaag)
  scheduled: number;       // Verwachte uren
  actual: number;          // Gewerkte uren
  leave: number;           // Verlofuren (countsAsSaldo)
  sick: number;            // Ziekteuren
  delta: number;           // actual + leave - scheduled
  cumulativeSaldo: number; // Lopend jaarsaldo inclusief beginsaldo
}

/**
 * Calculate year saldo for vast/min-max contracts.
 * Returns cumulative saldo (beginsaldo + mutations) and monthly breakdown.
 */
export function calculateYearSaldo(
  werkrooster: WerkroosterDay[] | null,
  team: string | null,
  year: number,
  timeEntries: { date: string; total_hours: number | null }[],
  leaveEntries: { date: string; hours: number; leave_type: string }[],
  beginsaldo: number,
  contractStart?: string | null,
  scheduleEntries?: { date: string; shift_type: string | null }[],
): { yearSaldo: number; monthlyBreakdown: MonthSaldo[] } | null {
  if (!werkrooster || werkrooster.length === 0) return null;

  const today = formatDate(new Date());
  const yearEnd = `${year}-12-31`;
  const clampTo = today > yearEnd ? yearEnd : today;

  const monthly: MonthSaldo[] = [];
  let cumulative = beginsaldo;

  for (let m = 0; m < 12; m++) {
    const monthStart = new Date(year, m, 1);
    const monthEnd = new Date(year, m + 1, 0); // last day of month
    const from = formatDate(monthStart);
    const to = formatDate(monthEnd);

    // Skip future months entirely
    if (from > clampTo) break;

    // Sla maanden vóór de datum in dienst helemaal over
    if (contractStart && to < contractStart) continue;

    // Clamp to today if month is in progress
    const effectiveTo = to > clampTo ? clampTo : to;

    const sched = calculateScheduledHours(
      werkrooster, team, from, effectiveTo,
      timeEntries, leaveEntries, contractStart, scheduleEntries,
    );

    // Sick hours from leave entries in this month
    const sickHours = leaveEntries
      .filter(l => l.date >= from && l.date <= effectiveTo && l.leave_type === 'ziekte')
      .reduce((sum, l) => sum + l.hours, 0);

    const delta = sched.saldo;
    cumulative += delta;

    monthly.push({
      month: m,
      year,
      label: `${MONTH_NAMES[m]} ${year}`,
      from,
      to: effectiveTo,
      scheduled: sched.totalScheduled,
      actual: sched.totalActual,
      leave: sched.totalLeave,
      sick: sickHours,
      delta,
      cumulativeSaldo: cumulative,
    });
  }

  return { yearSaldo: cumulative, monthlyBreakdown: monthly };
}

// ── Helpers ────────────────────────────────────────────────────────

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Format decimal hours to "Xu Ym" string
 */
export function formatHours(hours: number | null): string {
  if (hours === null || hours === undefined) return '-';
  const sign = hours < 0 ? '-' : '';
  const abs = Math.abs(hours);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return m > 0 ? `${sign}${h}u ${m}m` : `${sign}${h}u`;
}

/**
 * Format currency
 */
export function formatEuro(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
}
